// Loads Facebook avatar photos by URL into decoded ImageBitmaps.
//
// Unlike BlockBlast (which injects a bitmap into a shared ImagesHolder slot),
// SexyImagePuzzle's ImagesHolder/UIImage are ImgID/descriptor based, so we load
// per-URL bitmaps and hand them to UIImage.setImageBitmap. A small cache keyed by
// URL avoids refetching the same photo across the profile, selection grid and
// leaderboard rows.
//
// The cache is LRU-bounded: a 1000-player board could otherwise pin hundreds of
// full-size photos. Evicted bitmaps are not closed, an element still showing one
// keeps it alive and it is collected once nothing draws it.

const MAX_CACHED = 24;
const cache = new Map<string, Promise<ImageBitmap>>();

export function loadFacebookAvatar( url: string ): Promise<ImageBitmap>
{
  const cached = cache.get( url );
  if ( cached )
  {
    // Map keeps insertion order: re-insert to mark it as recently used.
    cache.delete( url );
    cache.set( url, cached );
    return cached;
  }

  const promise = fetch( url )
    .then( r => r.blob() )
    .then( blob => createImageBitmap( blob ) )
    .catch( e =>
    {
      // Drop failed entries so a later attempt can retry.
      cache.delete( url );
      console.warn( '[FacebookAvatarLoader] Failed to load:', e );
      throw e;
    } );

  cache.set( url, promise );
  while ( cache.size > MAX_CACHED )
    cache.delete( cache.keys().next().value as string );
  return promise;
}

export function clearFacebookAvatarCache(): void
{
  cache.clear();
}
